import { Spinner } from "@heroui/react";
import { type RestAPI } from "@sc07-canvas/lib";
import { type AuthSession, type ClientConfig } from "@sc07-canvas/lib/net";
import React, {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";

import { KeybindManager } from "../lib/keybinds";
import Network from "../lib/network";
import { PaletteLib } from "../lib/palette";
import { oapi } from "../lib/utils";

type Whoami = RestAPI.components["schemas"]["Whoami"];

interface ISettings {
  "canvas.zoomSensitivity"(val: number): number;
  "capture.cameraFlash"(val: boolean): boolean;
  "keybindings.enabled"(val: boolean): boolean;
  "presence.cursors"(val: boolean): boolean;
  "presence.reactions"(val: boolean): boolean;
  "palette.compact"(val: boolean | undefined): boolean | undefined;
  "palette.colors"(val: boolean): boolean;
  "palette.symbols"(val: boolean): boolean;
  "ui.adaptElementsToSidebar"(val: boolean): boolean;
  "ui.compactPaletteForSidebar"(val: boolean): boolean;
  "ui.compactButtons"(val: boolean): boolean;
  "ui.compactThemeSwitcher"(val: boolean): boolean;
  "ui.showOverlaysCard"(val: boolean): boolean;
  "ui.showThemeSwitcher"(val: boolean): boolean;
  "template.pixelCounts"(val: boolean): boolean;
  "template.hideUnusedColors"(val: boolean): boolean;
}

type SettingsAction<T extends keyof ISettings> = {
  key: T;
  value: Parameters<ISettings[T]>[0];
};

type ISettingsValue = {
  [K in keyof ISettings]: ReturnType<ISettings[K]>;
};

type TAppState = { chat: { shown: boolean } };
type TAppStateAction = { action: "chat_shown"; shown: boolean };

interface IAppContext {
  config?: ClientConfig;
  user?: AuthSession;
  whoami?: Whoami;
  connected: boolean;

  settings: {
    get: ISettingsValue;
    set: React.ActionDispatch<[SettingsAction<keyof ISettings>]>;
  };

  state: {
    get: TAppState;
    set: React.ActionDispatch<[TAppStateAction]>;
  };

  canvasPosition?: ICanvasPosition;
  setCanvasPosition: (v: ICanvasPosition) => void;
  cursor: ICursor;
  setCursorPos: (x: number | undefined, y: number | undefined) => void;
  undo?: { available: true; expireAt: number };

  loadChat: boolean;
  setLoadChat: (v: boolean) => void;

  blankOverlay: IMapOverlay;
  setBlankOverlay: React.Dispatch<React.SetStateAction<IMapOverlay>>;
  heatmapOverlay: IMapOverlay;
  setHeatmapOverlay: React.Dispatch<React.SetStateAction<IMapOverlay>>;
  pixelPulses: boolean;
  setPixelPulses: (state: boolean) => void;
  gridOverlay: IMapOverlay;
  setGridOverlay: React.Dispatch<React.SetStateAction<IMapOverlay>>;

  profile?: string; // sub
  setProfile: (v?: string) => void;
}

interface ICanvasPosition {
  x: number;
  y: number;
  zoom: number;
}

interface ICursor {
  x?: number;
  y?: number;
  color?: number;
  secondaryColor?: number;
}

interface IMapOverlay {
  enabled: boolean;

  /**
   * opacity of the overlay
   * 0.0 - 1.0
   */
  opacity: number;

  loading: boolean;
}

const appContext = createContext<IAppContext>({} as any);

const getStoredZoomSensitivity = () => {
  const value = Number(localStorage.getItem("canvas.zoomSensitivity"));
  return Number.isFinite(value) && value > 0
    ? Math.min(2, Math.max(0.25, value))
    : 1;
};

type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
  [Property in Key]-?: Type[Property];
};

type AppContext<ConfigExists extends boolean> = ConfigExists extends true
  ? WithRequiredProperty<IAppContext, "config">
  : IAppContext;

/**
 * Get app context
 *
 * @template ConfigExists If the config is already known to be available in this context
 * @returns
 */
export const useAppContext = <ConfigExists extends boolean = false>() =>
  useContext<AppContext<ConfigExists>>(appContext as any);

export const AppContext = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState<ClientConfig>(undefined as any);
  const [auth, setAuth] = useState<AuthSession>();
  const [whoami, setWhoami] = useState<Whoami>();
  const [canvasPosition, setCanvasPosition] = useState<ICanvasPosition>();
  const [cursor, setCursor] = useState<ICursor>({});
  const [connected, setConnected] = useState(false);

  // --- settings ---
  const [loadChat, _setLoadChat] = useState(false);

  const [undo, setUndo] = useState<{ available: true; expireAt: number }>();

  // overlays visible
  const [blankOverlay, setBlankOverlay] = useState<IMapOverlay>({
    enabled: false,
    opacity: 1,
    loading: false,
  });
  const [heatmapOverlay, setHeatmapOverlay] = useState<IMapOverlay>({
    enabled: false,
    opacity: 1,
    loading: false,
  });
  const [gridOverlay, setGridOverlay] = useState<IMapOverlay>({
    enabled: false,
    opacity: 0.1,
    loading: false,
  });
  const [pixelPulses, setPixelPulses] = useState<boolean>(false);

  const [profile, setProfile] = useState<string>();

  const settings = useReducer<
    ISettingsValue,
    [SettingsAction<keyof ISettings>]
  >(
    (state, action) => {
      return {
        ...state,
        [action.key]: action.value,
      };
    },
    {
      "canvas.zoomSensitivity": getStoredZoomSensitivity(),
      "capture.cameraFlash": true,
      "keybindings.enabled": true,
      "presence.cursors": localStorage.getItem("presence.cursors") !== "false",
      "presence.reactions":
        localStorage.getItem("presence.reactions") !== "false",
      "palette.compact": undefined,
      "palette.colors": false,
      "palette.symbols": false,
      "ui.adaptElementsToSidebar": true,
      "ui.compactPaletteForSidebar": true,
      "ui.compactButtons": false,
      "ui.compactThemeSwitcher": false,
      "ui.showOverlaysCard": true,
      "ui.showThemeSwitcher": true,
      "template.pixelCounts": false,
      "template.hideUnusedColors": false,
    },
  );

  useEffect(() => {
    localStorage.setItem(
      "canvas.zoomSensitivity",
      String(settings[0]["canvas.zoomSensitivity"]),
    );
  }, [settings[0]["canvas.zoomSensitivity"]]);

  useEffect(() => {
    localStorage.setItem(
      "presence.cursors",
      String(settings[0]["presence.cursors"]),
    );
  }, [settings[0]["presence.cursors"]]);

  useEffect(() => {
    localStorage.setItem(
      "presence.reactions",
      String(settings[0]["presence.reactions"]),
    );
  }, [settings[0]["presence.reactions"]]);

  const state = useReducer<TAppState, [TAppStateAction]>(
    (state, { action, ...data }) => {
      switch (action) {
        case "chat_shown":
          return {
            ...state,
            chat: {
              ...state.chat,
              shown: data.shown,
            },
          };
      }

      return state;
    },
    {
      chat: {
        shown: true,
      },
    },
  );

  const setCursorPos = (x: number | undefined, y: number | undefined) => {
    setCursor((c) => ({
      ...c,
      x,
      y,
    }));
  };

  useEffect(() => {
    const palette = PaletteLib.getInstance();
    const handleSelectedColor = (color: number | undefined) => {
      setCursor((c) => ({
        ...c,
        color,
      }));
    };
    const handleSecondaryColor = (secondaryColor: number | undefined) => {
      setCursor((c) => ({
        ...c,
        secondaryColor,
      }));
    };

    palette.on("selectedColor", handleSelectedColor);
    palette.on("secondaryColor", handleSecondaryColor);

    oapi.GET("/whoami").then(({ data }) => {
      setWhoami(data);
    });

    function loadSettings() {
      setLoadChat(
        localStorage.getItem("matrix.enable") === null
          ? true
          : localStorage.getItem("matrix.enable") === "true",
      );
    }

    function handleConfig(config: ClientConfig) {
      setConfig(config);
    }

    function handleUser(user: AuthSession) {
      setAuth(user);
    }

    function handleUndo(
      data: { available: false } | { available: true; expireAt: number },
    ) {
      if (data.available) {
        setUndo({ available: true, expireAt: data.expireAt });
      } else {
        setUndo(undefined);
      }
    }

    function handleConnect() {
      setConnected(true);
    }

    function handleDisconnect() {
      setConnected(false);
    }

    Network.on("user", handleUser);
    Network.on("config", handleConfig);
    Network.on("undo", handleUndo);

    Network.on("connected", handleConnect);
    Network.on("disconnected", handleDisconnect);

    Network.socket.connect();

    loadSettings();

    const toggleBlankOverlay = () => {
      setBlankOverlay((v) => ({ ...v, enabled: !v.enabled }));
    };

    KeybindManager.on("TOGGLE_BLANK", toggleBlankOverlay);

    return () => {
      palette.off("selectedColor", handleSelectedColor);
      palette.off("secondaryColor", handleSecondaryColor);
      KeybindManager.off("TOGGLE_BLANK", toggleBlankOverlay);

      Network.off("user", handleUser);
      Network.off("config", handleConfig);
      Network.off("undo", handleUndo);
      Network.off("connected", handleConnect);
      Network.off("disconnected", handleDisconnect);
    };
  }, []);

  const setLoadChat = (v: boolean) => {
    _setLoadChat(v);
    localStorage.setItem("matrix.enable", v ? "true" : "false");
  };

  return (
    <appContext.Provider
      value={{
        config,
        user: auth,
        whoami,
        settings: {
          get: settings[0],
          set: settings[1],
        },
        state: {
          get: state[0],
          set: state[1],
        },
        canvasPosition,
        setCanvasPosition,
        cursor,
        setCursorPos,
        undo,
        loadChat,
        setLoadChat,
        connected,
        blankOverlay,
        setBlankOverlay,
        heatmapOverlay,
        setHeatmapOverlay,
        gridOverlay,
        setGridOverlay,
        pixelPulses,
        setPixelPulses,
        profile,
        setProfile,
      }}
    >
      {!config && (
        <div className="fixed top-0 left-0 w-full h-full z-49 backdrop-blur-sm bg-black/30 text-white flex items-center justify-center">
          <Spinner label="Loading..." />
        </div>
      )}
      {children}
    </appContext.Provider>
  );
};
